import { useCallback, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Bell } from 'lucide-react';
import { notificationsApi, CadencEZNotification } from '@/lib/notifications';
import { useSocketEvent } from '@/hooks/useSocketEvent';

interface RealtimeNotification {
  id: string;
  kind: string;
  title: string;
  body: string;
  link?: string | null;
  workspaceId?: string | null;
  createdAt: string;
}

/**
 * Editorial bell. Polls every 60s as a safety net, but the canonical
 * update path is realtime: NotificationGateway pushes `notification.new`
 * on the user's socket the moment a row is persisted, and we invalidate
 * the cache so the dropdown re-hydrates instantly.
 */
export function NotificationBell() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);

  const { data: notifs = [] } = useQuery({
    queryKey: ['notifications', 'unread'],
    queryFn: () => notificationsApi.list(true),
    // Keep the 60s safety-net poll for when the socket drops; realtime
    // push will refresh the cache between polls when it's healthy.
    refetchInterval: 60_000,
    staleTime: 30_000,
  });

  const markRead = useMutation({
    mutationFn: (ids: string[]) => notificationsApi.markRead(ids),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  });

  // Realtime push handler — invalidate so React Query re-fetches the
  // canonical row shape (no client-side merge → no drift).
  const onRealtime = useCallback(() => {
    qc.invalidateQueries({ queryKey: ['notifications'] });
  }, [qc]);
  useSocketEvent<RealtimeNotification>('notification.new', onRealtime);

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        aria-label={t('nav.notifications')}
        className="relative h-9 w-9 inline-flex items-center justify-center rounded-md hover:bg-foreground/[0.04] text-muted-foreground transition-colors"
      >
        <Bell className="h-4 w-4" strokeWidth={1.8} />
        {notifs.length > 0 && (
          <span className="absolute -top-0.5 -end-0.5 min-w-[16px] h-4 px-1 rounded-full bg-emerald-600 text-white num text-[10px] leading-4 text-center font-semibold">
            {notifs.length > 9 ? '9+' : notifs.length}
          </span>
        )}
      </button>

      {open && (
        <div
          onMouseLeave={() => setOpen(false)}
          className="absolute end-0 mt-2 w-80 rounded-md border border-border bg-background shadow-editorial z-50"
        >
          <div className="px-4 py-3 border-b border-border flex items-center justify-between">
            <span className="text-sm font-semibold">{t('nav.notifications')}</span>
            {notifs.length > 0 && (
              <button
                onClick={() => markRead.mutate(notifs.map((n) => n.id))}
                className="text-[11.5px] text-emerald-700 hover:underline"
              >
                {t('notifications.mark_all_read')}
              </button>
            )}
          </div>
          <ul className="max-h-96 overflow-y-auto divide-y divide-border">
            {notifs.length === 0 ? (
              <li className="px-4 py-6 text-sm text-muted-foreground text-center">{t('notifications.empty')}</li>
            ) : (
              notifs.map((n) => <Item key={n.id} n={n} onRead={(id) => markRead.mutate([id])} t={t} />)
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

function Item({ n, onRead, t }: { n: CadencEZNotification; onRead: (id: string) => void; t: (k: string) => string }) {
  return (
    <li className="px-4 py-3 hover:bg-ink-50/50 transition-colors">
      <div className="text-[13px] font-medium">{n.title}</div>
      <p className="mt-0.5 text-[12px] text-muted-foreground leading-relaxed">{n.body}</p>
      <div className="mt-1.5 flex items-center justify-between">
        <span className="num text-[11px] text-muted-foreground/70">{new Date(n.createdAt).toLocaleString()}</span>
        <div className="flex gap-3">
          {n.link && <a href={n.link} className="text-[11.5px] text-emerald-700 hover:underline">{t('notifications.view')}</a>}
          <button onClick={() => onRead(n.id)} className="text-[11.5px] text-muted-foreground hover:text-foreground">{t('notifications.mark_read')}</button>
        </div>
      </div>
    </li>
  );
}
